import { useEffect, useMemo, useState } from 'react';
import { useRecoilState } from 'recoil';
import { Bell, ChevronLeft, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { profileState } from '@/state/user';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';

const ISIT_MODULES = [
  { key: 'it', path: 'wizard.form.modul.isit.it' },
  { key: 'is', path: 'wizard.form.modul.isit.is' },
  { key: 'ie', path: 'wizard.form.modul.isit.ie' },
  { key: 'ba', path: 'wizard.form.modul.isit.ba' },
  { key: 'se', path: 'wizard.form.modul.isit.se' },
  { key: 'eb', path: 'wizard.form.modul.isit.eb' }
];

const MANAGEMENT_MODULES = [
  { key: 'fm', path: 'wizard.form.modul.menadzment.fm' },
  { key: 'lo', path: 'wizard.form.modul.menadzment.lo' },
  { key: 'mk', path: 'wizard.form.modul.menadzment.mk' },
  { key: 'qs', path: 'wizard.form.modul.menadzment.qs' },
  { key: 'om', path: 'wizard.form.modul.menadzment.om' },
  { key: 'pm', path: 'wizard.form.modul.menadzment.pm' }
];

interface StudentProfile {
  year?: string;
  godina_studija?: string;
  smer?: string;
  modul?: string;
  akreditacija?: string;
  academic_profile_update_required?: boolean;
  academic_profile_current_cycle?: number | null;
  [key: string]: unknown;
}

function includesAny(value: string, terms: string[]) {
  const normalized = value.toLocaleLowerCase();
  return terms.some((term) => normalized.includes(term));
}

export function AnnualProfileWizard() {
  const { t, i18n } = useTranslation();
  const [profile, setProfile] = useRecoilState<StudentProfile | null>(profileState);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);
  const [submitting, setSubmitting] = useState(false);
  const [studyLevel, setStudyLevel] = useState('');
  const [studyYear, setStudyYear] = useState('');
  const [program, setProgram] = useState('');
  const [accreditation, setAccreditation] = useState('');
  const [module, setModule] = useState('');
  const [customModule, setCustomModule] = useState('');

  const localPreview =
    typeof window !== 'undefined' &&
    ['localhost', '127.0.0.1'].includes(window.location.hostname) &&
    new URLSearchParams(window.location.search).has('annual-profile-preview');
  const required = Boolean(
    profile?.academic_profile_update_required || localPreview
  );

  const moduleOptions = useMemo(() => {
    if (program === 'ISIT') return ISIT_MODULES;
    if (program === 'Menadzment') return MANAGEMENT_MODULES;
    return [];
  }, [program]);

  const showModuleSelect =
    studyLevel === 'OAS' &&
    accreditation === 'nova' &&
    Boolean(program);
  const showCustomModule =
    ((studyLevel === 'MAS' || studyLevel === 'SAS') && Boolean(program)) ||
    (studyLevel === 'OAS' && accreditation === 'stara' && Boolean(program));
  const moduleRequired = Number(studyYear) >= 2;
  const hasRequiredModule =
    !moduleRequired ||
    (showModuleSelect ? Boolean(module) : Boolean(customModule.trim()));

  useEffect(() => {
    if (!open || !profile) return;

    const level = String(profile.year || '');
    if (includesAny(level, ['oas', 'undergraduate', 'osnovne'])) {
      setStudyLevel('OAS');
    } else if (includesAny(level, ['mas', 'master'])) {
      setStudyLevel('MAS');
    } else if (includesAny(level, ['sas', 'specialist', 'specijalističke'])) {
      setStudyLevel('SAS');
    }

    setStudyYear(String(profile.godina_studija || ''));

    const savedProgram = String(profile.smer || '');
    if (includesAny(savedProgram, ['isit', 'information', 'informacioni'])) {
      setProgram('ISIT');
    } else if (
      includesAny(savedProgram, ['management', 'menadžment', 'menadzment'])
    ) {
      setProgram('Menadzment');
    }

    const savedAccreditation = String(profile.akreditacija || '');
    if (includesAny(savedAccreditation, ['2023', 'nova', 'new'])) {
      setAccreditation('nova');
    } else if (includesAny(savedAccreditation, ['2014', 'stara', 'old'])) {
      setAccreditation('stara');
    }

    const savedModule = String(profile.modul || '');
    if (savedModule) {
      const allModules = [...ISIT_MODULES, ...MANAGEMENT_MODULES];
      const match = allModules.find(({ path }) =>
        ['en-US', 'sr-RS'].some(
          (locale) => i18n.getFixedT(locale)(path) === savedModule
        )
      );
      if (match) setModule(match.key);
      else setCustomModule(savedModule);
    }
  }, [open, profile, i18n]);

  // A tab can remain open across midnight on 1 October, and bootstrap may
  // temporarily degrade during a deploy. Fetch immediately even when Recoil
  // has no profile yet, then refresh on focus/online and hourly so the server's
  // Belgrade-time decision reaches the UI without requiring a manual reload.
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const response = await fetch('/api/profile/get', { method: 'POST' });
        if (!response.ok) return;
        const data = await response.json();
        if (!cancelled && data.profile) setProfile(data.profile);
      } catch {
        // A temporary network failure must not clear an already-visible reminder.
      }
    };

    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };

    void refresh();
    const interval = window.setInterval(refresh, 60 * 60 * 1000);
    window.addEventListener('focus', refresh);
    window.addEventListener('online', refresh);
    document.addEventListener('visibilitychange', refreshWhenVisible);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('online', refresh);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [setProfile]);

  const studyYears = studyLevel === 'OAS' ? ['1', '2', '3', '4'] : ['1', '2'];

  const save = async () => {
    if (
      !studyLevel ||
      !studyYear ||
      !program ||
      !accreditation ||
      !hasRequiredModule
    ) {
      toast.error(t('wizard.annual.errors.required'));
      return;
    }

    const selectedModule = moduleOptions.find((item) => item.key === module);
    const moduleText = showModuleSelect
      ? selectedModule
        ? t(selectedModule.path)
        : ''
      : customModule.trim();

    setSubmitting(true);
    try {
      const response = await fetch('/api/profile/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          year: t(`wizard.form.year.options.${studyLevel.toLowerCase()}`),
          godina_studija: studyYear,
          smer: t(`wizard.form.smer.options.${program.toLowerCase()}`),
          akreditacija: t(`wizard.form.akreditacija.options.${accreditation}`),
          modul: moduleText,
          confirm_academic_profile: true
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || t('wizard.annual.errors.save'));

      setProfile(data.profile);
      if (localPreview) {
        const url = new URL(window.location.href);
        url.searchParams.delete('annual-profile-preview');
        window.history.replaceState({}, '', url);
      }
      setOpen(false);
      setStep(1);
      window.dispatchEvent(new Event('profile-updated'));
      toast.success(t('wizard.annual.success'));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('wizard.annual.errors.save'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!required) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="absolute left-1/2 top-5 z-40 flex w-[calc(100%-2rem)] max-w-2xl -translate-x-1/2 items-center gap-3 rounded-2xl border border-primary/30 bg-surface/95 px-4 py-3 text-left shadow-2xl backdrop-blur-xl transition hover:border-primary/60"
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-background">
          <Bell className="h-5 w-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-semibold text-primary">
            {t('wizard.annual.notification.title')}
          </span>
          <span className="block text-sm text-secondary">
            {t('wizard.annual.notification.description')}
          </span>
        </span>
        <span className="hidden shrink-0 text-sm font-semibold text-primary sm:block">
          {t('wizard.annual.notification.action')}
        </span>
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/75 p-4 backdrop-blur-md">
          <div className="panel relative w-full max-w-xl rounded-3xl border border-outline p-6 shadow-2xl sm:p-8">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="fongpt-hover absolute right-5 top-5 rounded-full p-2 text-secondary transition"
              aria-label={t('wizard.annual.close')}
            >
              <X className="h-5 w-5" />
            </button>

            <div className="mb-7 pr-10">
              <p className="mb-2 text-sm font-medium text-secondary">
                {t('wizard.annual.step', { current: step, total: 2 })}
              </p>
              <h2 className="text-2xl font-semibold text-primary sm:text-3xl">
                {step === 1
                  ? t('wizard.annual.study.title')
                  : t('wizard.annual.module.title')}
              </h2>
              <p className="mt-2 text-secondary">
                {step === 1
                  ? t('wizard.annual.study.description')
                  : t('wizard.annual.module.description')}
              </p>
            </div>

            {step === 1 ? (
              <div className="space-y-5">
                <div className="space-y-2">
                  <Label>{t('wizard.form.year.label')}</Label>
                  <Select
                    value={studyLevel}
                    onValueChange={(value) => {
                      setStudyLevel(value);
                      setStudyYear('');
                    }}
                  >
                    <SelectTrigger><SelectValue placeholder={t('wizard.form.year.placeholder')} /></SelectTrigger>
                    <SelectContent className="z-[70]">
                      {['OAS', 'MAS', 'SAS'].map((value) => (
                        <SelectItem key={value} value={value}>
                          {t(`wizard.form.year.options.${value.toLowerCase()}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('wizard.form.godinaStudija.label')}</Label>
                  <Select value={studyYear} onValueChange={setStudyYear} disabled={!studyLevel}>
                    <SelectTrigger><SelectValue placeholder={t('wizard.form.godinaStudija.placeholder')} /></SelectTrigger>
                    <SelectContent className="z-[70]">
                      {studyYears.map((value) => (
                        <SelectItem key={value} value={value}>
                          {value}. {t('wizard.form.godinaStudija.godina')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="button"
                  className="w-full"
                  disabled={!studyLevel || !studyYear}
                  onClick={() => setStep(2)}
                >
                  {t('wizard.annual.next')}
                </Button>
              </div>
            ) : (
              <div className="space-y-5">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>{t('wizard.form.smer.label')}</Label>
                    <Select
                      value={program}
                      onValueChange={(value) => {
                        setProgram(value);
                        setModule('');
                        setCustomModule('');
                      }}
                    >
                      <SelectTrigger><SelectValue placeholder={t('wizard.form.smer.placeholder')} /></SelectTrigger>
                      <SelectContent className="z-[70]">
                        <SelectItem value="ISIT">{t('wizard.form.smer.options.isit')}</SelectItem>
                        <SelectItem value="Menadzment">{t('wizard.form.smer.options.menadzment')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('wizard.form.akreditacija.label')}</Label>
                    <Select value={accreditation} onValueChange={setAccreditation}>
                      <SelectTrigger><SelectValue placeholder={t('wizard.form.akreditacija.placeholder')} /></SelectTrigger>
                      <SelectContent className="z-[70]">
                        <SelectItem value="nova">{t('wizard.form.akreditacija.options.nova')}</SelectItem>
                        <SelectItem value="stara">{t('wizard.form.akreditacija.options.stara')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {showModuleSelect && (
                  <div className="space-y-2">
                    <Label>
                      {moduleRequired
                        ? t('wizard.annual.module.requiredLabel')
                        : t('wizard.form.modul.labelOptional')}
                    </Label>
                    <Select value={module} onValueChange={setModule}>
                      <SelectTrigger><SelectValue placeholder={t('wizard.form.modul.placeholder')} /></SelectTrigger>
                      <SelectContent className="z-[70]">
                        {moduleOptions.map((item) => (
                          <SelectItem key={item.key} value={item.key}>{t(item.path)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {showCustomModule && (
                  <div className="space-y-2">
                    <Label>
                      {moduleRequired
                        ? t('wizard.annual.module.requiredLabel')
                        : t('wizard.form.modul.labelOptional')}
                    </Label>
                    <Input
                      value={customModule}
                      onChange={(event) => setCustomModule(event.target.value)}
                      placeholder={t('wizard.form.modul.placeholderCustom')}
                    />
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setStep(1)}>
                    <ChevronLeft className="mr-1 h-4 w-4" />
                    {t('wizard.annual.back')}
                  </Button>
                  <Button
                    type="button"
                    className="flex-1"
                    disabled={
                      submitting ||
                      !program ||
                      !accreditation ||
                      !hasRequiredModule
                    }
                    onClick={save}
                  >
                    {submitting ? t('wizard.form.actions.saving') : t('wizard.annual.confirm')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
